// Watch parties & listening rooms: shared playback state carried in encrypted messages.
// Pure functions, so the sync rules are unit-tested.
import type { Party, PartyMedia } from '../api/types'
import type { StoredMessage } from '../storage/db'

export interface ActiveParty {
  party: Party
  /** Who sent the latest event (user id). */
  by: string
  /** Who started it. */
  host: string
  startedSeq: number
}

/** The party currently running in a conversation, if any (latest event wins). */
export function currentParty(msgs: StoredMessage[]): ActiveParty | null {
  let active: ActiveParty | null = null
  for (const m of msgs) {
    if (m.envelope.type !== 'party') continue
    const p = m.envelope.party
    if (p.action === 'start') {
      active = { party: p, by: m.sender.user_id, host: m.sender.user_id, startedSeq: m.seq }
    } else if (active !== null && p.id === active.party.id) {
      const prev: ActiveParty = active
      active = p.action === 'end' ? null : { ...prev, party: p, by: m.sender.user_id }
    }
  }
  return active
}

/** All party start events in a conversation, with whether each is still running. */
export function partyStarts(msgs: StoredMessage[]): Map<string, boolean> {
  const out = new Map<string, boolean>()
  for (const m of msgs) {
    if (m.envelope.type !== 'party') continue
    const p = m.envelope.party
    if (p.action === 'start') out.set(p.id, true)
    if (p.action === 'end') out.set(p.id, false)
  }
  return out
}

/** Where playback should be (ms) at server time `serverNow`. */
export function expectedPosition(p: Party, serverNow: number): number {
  if (!p.playing) return p.position_ms
  return p.position_ms + Math.max(0, serverNow - p.at_ms)
}

export interface Correction {
  /** Seek to this many seconds, if set. */
  seek?: number
  /** Playback rate to apply (1 = normal). */
  rate: number
}

/** Small drift is absorbed by nudging the rate; large drift (or pause) by seeking. */
export function correction(localSecs: number, expectedSecs: number, playing: boolean): Correction {
  const drift = expectedSecs - localSecs
  if (!playing) return Math.abs(drift) > 0.25 ? { seek: expectedSecs, rate: 1 } : { rate: 1 }
  if (Math.abs(drift) > 1.0) return { seek: expectedSecs, rate: 1 }
  if (Math.abs(drift) > 0.15) return { rate: drift > 0 ? 1.05 : 0.95 }
  return { rate: 1 }
}

/** Tracks the offset between this device's clock and the server's (ms). */
export class ServerClock {
  private best = Number.POSITIVE_INFINITY

  /** Record a server timestamp observed at local time `localNow`. The minimum of
   * local-minus-server is the best estimate (it includes the least network delay). */
  observe(serverMs: number, localNow = Date.now()): void {
    this.best = Math.min(this.best, localNow - serverMs)
  }

  get offset(): number {
    return Number.isFinite(this.best) ? this.best : 0
  }

  serverNow(localNow = Date.now()): number {
    return localNow - this.offset
  }
}

export function mediaTitle(m: PartyMedia | undefined): string {
  if (!m) return ''
  return m.source === 'library' ? m.item.title : m.media.name
}

export function mediaKindOf(m: PartyMedia | undefined): 'audio' | 'video' {
  if (!m) return 'video'
  if (m.source === 'library') return m.item.kind === 'audio' ? 'audio' : 'video'
  return m.media.mime.startsWith('audio/') ? 'audio' : 'video'
}

export function newPartyId(): string {
  return crypto.randomUUID()
}

// --- Liveness ---------------------------------------------------------------------------

/**
 * A party is live when it's the conversation's current one, hasn't been ended, and someone
 * is in it (server presence). Without anyone present it was abandoned (tabs closed without
 * "end"), so it can't be joined, only started again.
 */
export function isLive(msgs: StoredMessage[], partyId: string, present: readonly string[]): boolean {
  const cur = currentParty(msgs)
  return !!cur && cur.party.id === partyId && present.length > 0
}

/** Who keeps everyone in sync by repeating the state: the first person present (by id). */
export function syncLeader(present: readonly string[], host: string): string {
  return [...present].sort()[0] ?? host
}

// --- Shared queue ----------------------------------------------------------------------
// Everyone in a room can change the queue. Each edit keeps `index` on the item that's
// playing, so changing what's next never interrupts what's on now.

export interface QueueState {
  queue: PartyMedia[]
  index: number
}

/** Adds to the end, or right after the current item (`next`). */
export function addToQueue(s: QueueState, m: PartyMedia, next = false): QueueState {
  const at = next ? s.index + 1 : s.queue.length
  return { queue: [...s.queue.slice(0, at), m, ...s.queue.slice(at)], index: s.index }
}

/**
 * Removes item `i`. Removing what's playing moves on to the item after it (or the one
 * before, at the end). The last item can't be removed: end the room instead.
 */
export function removeFromQueue(s: QueueState, i: number): QueueState | null {
  if (s.queue.length <= 1 || i < 0 || i >= s.queue.length) return null
  const queue = s.queue.filter((_, j) => j !== i)
  const index = i < s.index ? s.index - 1 : Math.min(s.index, queue.length - 1)
  return { queue, index }
}

/** Moves item `from` to position `to`, keeping the current item current. */
export function moveInQueue(s: QueueState, from: number, to: number): QueueState {
  if (from === to || from < 0 || to < 0 || from >= s.queue.length || to >= s.queue.length) return s
  const queue = [...s.queue]
  const [m] = queue.splice(from, 1)
  queue.splice(to, 0, m!)
  let index = s.index
  if (from === s.index) index = to
  else if (from < s.index && to >= s.index) index -= 1
  else if (from > s.index && to <= s.index) index += 1
  return { queue, index }
}

/** Runs calls one after another, in the order they were made (each waits for the last). */
export function serially(): <T>(call: () => Promise<T>) => Promise<T> {
  let queue: Promise<unknown> = Promise.resolve()
  return (call) => {
    const next = queue.then(call, call)
    queue = next.catch(() => undefined)
    return next
  }
}
